// apps/web/src/features/russia-requests/components/editor/RussiaEditorStartCitiesCard.tsx
//
// Multi-city picker. Reads the active arrival-city catalog via
// `useActiveArrivalCities`, mirrors china-side composition by living next
// to its own Card chrome, and stores the selected ids as an ordered
// array under RHF's `startCityIds` field.

import { useMemo, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CreateRussiaRequestManualInput } from '@magimp/shared';
import { Button, IconButton, Select } from '../../../../ui/index.js';
import { useActiveArrivalCities, type ActiveArrivalCity } from '../../../../catalog/index.js';
import { Card, Field, chipStyle } from './EditorPrimitives.js';

export interface RussiaEditorStartCitiesCardProps {
  form: UseFormReturn<CreateRussiaRequestManualInput>;
  onChanged: () => void;
}

export function RussiaEditorStartCitiesCard({ form, onChanged }: RussiaEditorStartCitiesCardProps) {
  const { t } = useTranslation('russiaRequests');
  const { control } = form;

  const citiesQuery = useActiveArrivalCities();
  const allCities = useMemo<ActiveArrivalCity[]>(() => citiesQuery.data ?? [], [citiesQuery.data]);
  const byId = useMemo(() => new Map(allCities.map((c) => [c.id, c])), [allCities]);
  const [picker, setPicker] = useState('');

  return (
    <Card title={t('editor.cards.startCities')}>
      <Field label={t('editor.fields.startCities')}>
        <Controller
          control={control}
          name="startCityIds"
          render={({ field }) => {
            const startCityIds = field.value;
            const selectedSet = new Set(startCityIds);
            const remaining = allCities.filter((c) => !selectedSet.has(c.id));

            function addCity(id: string): void {
              if (!id || selectedSet.has(id)) return;
              field.onChange([...startCityIds, id]);
              setPicker('');
              onChanged();
            }

            function removeCity(id: string): void {
              field.onChange(startCityIds.filter((c) => c !== id));
              onChanged();
            }

            return (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {startCityIds.length === 0 ? (
                    <span style={{ fontSize: 13, color: 'var(--fg-3)' }}>
                      {t('editor.startCities.empty')}
                    </span>
                  ) : (
                    startCityIds.map((id) => {
                      const city = byId.get(id);
                      const label = city ? city.name : id;
                      return (
                        <span
                          key={id}
                          data-testid={`russia-requests-editor-startCity-chip-${id}`}
                          style={chipStyle}
                        >
                          {label}
                          <IconButton
                            icon="x"
                            size={20}
                            label={t('editor.startCities.remove', { name: label })}
                            onClick={() => removeCity(id)}
                          />
                        </span>
                      );
                    })
                  )}
                </div>
                {remaining.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Select
                      value={picker}
                      onChange={setPicker}
                      options={[
                        { value: '', label: t('editor.startCities.pickerPlaceholder') },
                        ...remaining.map((c) => ({ value: c.id, label: c.name })),
                      ]}
                      placeholder={t('editor.startCities.pickerPlaceholder')}
                    />
                    <Button
                      type="button"
                      onClick={() => addCity(picker)}
                      disabled={!picker}
                      data-testid="russia-requests-editor-startCity-add"
                    >
                      {t('editor.startCities.add')}
                    </Button>
                  </div>
                )}
              </>
            );
          }}
        />
      </Field>
    </Card>
  );
}
